import {
  isApiError,
  NETWORK_ERROR_CODE,
  NETWORK_TIMEOUT_CODE,
  toUserMessage,
} from '@betboom/api-contract/errors';
import { env } from '@/shared/config';
import { TelegramUnavailableError } from '@/shared/lib/telegram';

/**
 * Что показать игроку, когда запрос не удался.
 *
 * Сообщение сервера игроку **не показывается никогда**. Оно написано
 * разработчиком для разработчика, и это видно по содержимому: до этого файла
 * игрок читал «Активный сезон не найден. Заведите сезон и циклы через сиды»,
 * «Ни один Недельный цикл не покрывает момент 2026-09-14T21:00:00.000Z»
 * и «Игрок 42 не найден» — с внутренним идентификатором.
 *
 * Отличие от админки (`apps/admin/src/shared/api/error-text.ts`): там текст
 * сервера остаётся на экране намеренно, оператор пересказывает его
 * разработчику. У игрока такой роли нет.
 *
 * ТЕКСТЫ — ЧЕРНОВИКИ СТУДИИ. Копирайта заказчика нет (открытый вопрос 4),
 * тексты пишутся тоном шаблонов бота: обращение на «ты», термины ГДД
 * с большой буквы. Заменяются целиком, без правки кода вокруг.
 * Сводка для заказчика — `docs/player-errors.md`.
 *
 * ФОРМА ЗАПИСЕЙ ГРЕППАБЕЛЬНАЯ И ДОЛЖНА ТАКОЙ ОСТАТЬСЯ: ключ — строковый
 * литерал, по одному на запись. По ним ходит инвариант
 * `pnpm fe check:invariants`, который следит, что каждый код доменной ошибки
 * бэкенда имеет либо текст здесь, либо причину в `NO_OWN_TEXT`.
 */
export interface PlayerErrorText {
  /** Одна-две короткие фразы: место на экране не резиновое. */
  text: string;
  /**
   * Правило игры или поломка.
   *
   * «Приём Прогнозов закрыт» — правило, игрок ничего не сломал.
   * Пока различие видно только в `docs/player-errors.md` и в том, что
   * на правилах не пишется предупреждение в консоль; разной вёрстки нет.
   */
  kind: 'rule' | 'fault';
}

/** Коды, у которых свой текст. Порядок — по модулям бэкенда. */
const PLAYER_ERROR_TEXTS: Record<string, PlayerErrorText> = {
  [NETWORK_ERROR_CODE]: {
    text: 'Нет связи. Проверь интернет и попробуй ещё раз.',
    kind: 'fault',
  },
  [NETWORK_TIMEOUT_CODE]: {
    text: 'Сервер долго не отвечает. Попробуй ещё раз через минуту.',
    kind: 'fault',
  },

  'auth.invalid_init_data': {
    text: 'Не получилось подтвердить вход. Закрой игру и открой заново через бота BetBoom.',
    kind: 'fault',
  },
  'auth.unauthorized': {
    text: 'Нужно войти заново. Открой игру через бота BetBoom.',
    kind: 'fault',
  },

  'users.not_found': {
    text: 'Аккаунт не найден. Открой игру заново через бота BetBoom.',
    kind: 'fault',
  },
  'users.blocked': {
    text: 'Аккаунт заблокирован. Напиши в поддержку BetBoom, если это ошибка.',
    kind: 'rule',
  },

  // 503 до старта кампании и после её конца — это расписание, а не сбой
  'cycles.no_active_season': {
    text: 'Сезон ещё не начался. Бот напишет, когда всё откроется.',
    kind: 'rule',
  },
  'cycles.no_cycle_for_moment': {
    text: 'Игровой недели сейчас нет. Загляни позже.',
    kind: 'rule',
  },
  'reward_track.chapter_not_found': {
    text: 'Глава Карьеры на этот месяц ещё не открыта. Загляни позже.',
    kind: 'rule',
  },

  'predictions.closed': {
    text: 'Приём Прогнозов на этот матч уже закрыт.',
    kind: 'rule',
  },
  'predictions.not_open': {
    text: 'Приём Прогнозов на этот матч ещё не открыт.',
    kind: 'rule',
  },
  'predictions.not_in_pool': {
    text: 'Сегодня на этот матч Прогнозов нет. Посмотри, что доступно в Серии.',
    kind: 'rule',
  },
  'predictions.duplicate': {
    text: 'Такой Прогноз на этот матч уже сделан: по одному Прогнозу каждого типа.',
    kind: 'rule',
  },
  'predictions.type_unavailable': {
    text: 'Этот тип Прогноза сейчас недоступен.',
    kind: 'rule',
  },
  // 400: клиент отправил то, чего не бывает. Игроку это выглядит как сбой
  'predictions.invalid_choice': {
    text: 'Так не получится — проверь выбор и попробуй ещё раз.',
    kind: 'fault',
  },

  'points.not_enough_funcoins': {
    text: 'Не хватает Фанкоинов на этот Прогноз. Зарабатывай их за активность в сообществе.',
    kind: 'rule',
  },

  'sports.match_not_found': {
    text: 'Матч не найден — возможно, его убрали из Серии. Обнови экран.',
    kind: 'rule',
  },

  'derby.none_active': {
    text: 'Сейчас нет доступного Дерби. Бот напишет, когда откроется следующее.',
    kind: 'rule',
  },
  'derby.already_finished': {
    text: 'Это Дерби ты уже прошёл. Следующее будет по расписанию.',
    kind: 'rule',
  },
  'derby.answer_locked': {
    text: 'Ответ уже дан и не меняется.',
    kind: 'rule',
  },
  'derby.question_expired': {
    text: 'Время на вопрос вышло — он засчитан как неверный.',
    kind: 'rule',
  },
  'derby.out_of_order': {
    text: 'Отвечать нужно по порядку. Обнови экран.',
    kind: 'rule',
  },
  'derby.question_not_in_derby': {
    text: 'Этот вопрос из другого Дерби. Обнови экран.',
    kind: 'fault',
  },

  // Право на Фрибеты не сгорает ни при каком неуспехе — обещать иное нельзя
  'freebets.no_claim_available': {
    text: 'Фрибеты пока не готовы к выдаче. Бот напишет, когда букмекер подтвердит: право на Фрибеты не сгорает.',
    kind: 'rule',
  },

  'results.unknown_result': {
    text: 'Этих итогов уже нет. Обнови экран.',
    kind: 'fault',
  },
};

/**
 * Группы кодов с общим текстом: префикс → текст.
 *
 * `db.*` — ошибки уровня базы, бывшие 500. Для игрока любая из них означает
 * баг или гонку, отдельного сценария в ГДД под них нет.
 */
const GROUP_TEXTS: Record<string, PlayerErrorText> = {
  'db.': {
    text: 'Не получилось сохранить. Попробуй ещё раз через минуту.',
    kind: 'fault',
  },
};

/**
 * Коды, которым свой текст не нужен, и почему.
 *
 * Пустая строка причиной не считается: инвариант требует объяснения, иначе
 * список превращается в место, куда сваливают новые коды не думая.
 *
 * Экспортируется ради самого списка, а не ради вызова: читает его инвариант
 * `check-invariants.mjs`, разбирая исходник, а не импортируя модуль.
 */
export const NO_OWN_TEXT: Record<string, string> = {
  'auth.forbidden': 'только из админки: роли есть у неё, у мини-аппа их нет',
  'admin.self_demotion':
    'только из админки: снятие прав с собственной учётной записи',
  'sports.match_already_settled':
    'только из админки: правка итога рассчитанного матча',
  'achievements.not_found': 'только из админки: редактор Ачивок',
  'admin.game_setting_invalid_value': 'только из админки: редактор настроек',
  'admin.game_setting_missing': 'только из админки: редактор настроек',
  'admin.game_setting_type': 'только из админки: редактор настроек',
  'admin.game_setting_unknown_key': 'только из админки: редактор настроек',
  'admin.login_taken': 'только из админки: раздел «Доступ»',
  'bot.broadcast_no_recipients': 'только из админки: массовая рассылка',
  'bot.broadcast_not_found': 'только из админки: массовая рассылка',
  'bot.broadcast_already_closed': 'только из админки: массовая рассылка',
  'freebets.manual_delivery_disabled':
    'только из админки: выдача Фрибетов таблицей',
  'freebets.manual_batch_open': 'только из админки: выдача Фрибетов таблицей',
  'freebets.nothing_to_deliver': 'только из админки: выдача Фрибетов таблицей',
  'freebets.manual_batch_not_found':
    'только из админки: выдача Фрибетов таблицей',
  'freebets.manual_batch_closed': 'только из админки: выдача Фрибетов таблицей',
  'freebets.manual_batch_unknown_users':
    'только из админки: выдача Фрибетов таблицей',
  'admin.player_not_found': 'только из админки: поиск игрока',
  'admin.self_deactivation': 'только из админки: раздел «Доступ»',
  'admin.user_not_found': 'только из админки: раздел «Доступ»',
  'bot.template_body_empty': 'только из админки: тексты бота',
  'bot.template_buttons_invalid': 'только из админки: тексты бота',
  'bot.template_caption_too_long': 'только из админки: тексты бота',
  'bot.broadcast_caption_too_long': 'только из админки: рассылки',
  'bot.broadcast_image_missing': 'только из админки: рассылки',
  'bot.broadcast_image_unsupported_type': 'только из админки: рассылки',
  'bot.broadcast_image_too_large': 'только из админки: рассылки',
  'bot.broadcast_image_write_failed': 'только из админки: рассылки',
  'bot.broadcast_image_public_url_unknown': 'только из админки: рассылки',
  'bot.player_blocked':
    'только из админки: тестовая отправка шаблона игроку по нику',
  'bot.player_by_username_not_found':
    'только из админки: тестовая отправка шаблона игроку по нику',
  'bot.template_not_found': 'только из админки: тексты бота',
  'derby.duplicate_code': 'только из админки: редактор Дерби',
  'derby.has_attempts': 'только из админки: редактор Дерби',
  'derby.ladder_incomplete': 'только из админки: редактор Дерби',
  'derby.reward_missing':
    '500: лестница наград Дерби не заполнена. Это наш баг, игроку — общий текст про сбой',
  'draws.cycle_already_drawn': 'только из админки: призы розыгрышей',
  'draws.cycle_not_found': 'только из админки: призы розыгрышей',
  'draws.prize_not_found': 'только из админки: призы розыгрышей',
  'draws.unknown_pool_type': 'только из админки: призы розыгрышей',
  'points.invalid_amount':
    '500: отрицательные базовые Очки в конфиге. Наш баг, игроку — общий текст про сбой',
  'predictions.duplicate_series_code': 'только из админки: редактор Серий',
  'predictions.series_not_found': 'только из админки: редактор Серий',
  'season_final.already_conducted': 'только из админки: Финал сезона',
  'season_final.no_candidates': 'только из админки: Финал сезона',
};

/** Запасные тексты, когда код незнаком. Ключи литеральные — не индексируются строкой. */
const UNKNOWN = {
  auth: {
    text: 'Нужно войти заново. Открой игру через бота BetBoom.',
    kind: 'fault',
  },
  server: {
    text: 'На нашей стороне сбой. Попробуй через пару минут.',
    kind: 'fault',
  },
  request: {
    text: 'Так не получится — проверь, что ввёл.',
    kind: 'fault',
  },
  other: {
    text: 'Действие сейчас недоступно. Обнови экран и попробуй ещё раз.',
    kind: 'fault',
  },
} satisfies Record<string, PlayerErrorText>;

/**
 * Чего не хватает в словаре — видит разработчик, не игрок.
 *
 * Строка сервера уходит сюда и только сюда: в прод-сборке ветка выпадает
 * вместе с `env.isDev`.
 */
function warnUncovered(code: string, status: number, message: string): void {
  if (!env.isDev) return;
  console.warn(`[api] нет текста для ${code} (${String(status)}): ${message}`);
}

/**
 * Ошибка → текст для игрока.
 *
 * Порядок ступеней важен: словарь стоит **выше** статусов. `503` у
 * `cycles.no_active_season` для игрока не сбой, а «сезон ещё не начался»,
 * и статусная ветка сказала бы неправду.
 */
export function describePlayerError(error: unknown): PlayerErrorText {
  // Свой текст, уже написанный для игрока
  if (error instanceof TelegramUnavailableError) {
    return { text: error.message, kind: 'rule' };
  }

  if (!isApiError(error)) {
    return { text: toUserMessage(error), kind: 'fault' };
  }

  const known = PLAYER_ERROR_TEXTS[error.code];
  if (known) return known;

  for (const [prefix, group] of Object.entries(GROUP_TEXTS)) {
    if (error.code.startsWith(prefix)) return group;
  }

  warnUncovered(error.code, error.status, error.message);

  if (error.isUnauthorized) return UNKNOWN.auth;
  if (error.status >= 500 || error.status === 0) return UNKNOWN.server;
  if (error.status === 400) return UNKNOWN.request;
  return UNKNOWN.other;
}

/** Короткая форма: одна строка для `error` в сторе или в форме. */
export function toPlayerMessage(error: unknown): string {
  return describePlayerError(error).text;
}
